import { useEffect, useState } from 'react';

import Form from 'components/Form';
import { IFormRegister, IFormValues, IFormWatch } from 'routes/Main/type';

import RowContainer from '../../components/RowContainer';
import RowContent from '../../components/RowContent';
import RowContentPrewrap from './components/RowContentPrewrap';
import SectionTitle from '../../components/SectionTitle';

import { Box, MenuItem, FormControlLabel, Checkbox } from '@mui/material';

interface Props extends IFormRegister, IFormValues, IFormWatch {
  disabled?: boolean;
}

const NewOpInfo = (props: Props) => {
  const { disabled, register, watch, setValue, getValues } = props;

  const [postureEtc, setPostureEtc] = useState(0);

  const position_labels = [
    'Nằm ngửa',
    'Nằm đầu thấp',
    'Nằm đầu cao',
    'Fowler’s',
    'Semi-Fowler’s',
    'phụ khoa',
    'Nằm nghiêngl (trái)',
    'Nằm nghiêng (phải)',
    'nằm sấp',
    'Jackknife',
    'Nhập trực tiếp',
  ];

  const contents = [
    {
      label: 'Ngày phẫu thuật',
      element: (
        <Form.MuiTextField
          type="date"
          InputLabelProps={{ shrink: true }}
          required={false}
          disabled={disabled}
          {...register('operation_information.operating_date')}
        />
      ),
    },
    {
      label: 'Chẩn đoán sơ bộ',
      element: (
        <Form.MuiTextField
          {...register('operation_information.operating_department')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Chẩn đoán sau\nphẫu thuật',
      element: (
        <Form.MuiTextField
          {...register('operation_information.operating_time')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Tên phẫu thuật',
      element: (
        <Form.MuiTextField
          {...register('operation_information.asa_class')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Vị trí',
      element: (
        <Form.MuiTextField
          {...register('operation_information.main_operation_name')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Tình trạng da',
      element: (
        <Form.MuiTextField
          {...register('operation_information.minor_operation_name')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Chuẩn bị da',
      element: (
        <Form.MuiTextField
          {...register('operation_information.past_history')}
          required={false}
          disabled={disabled}
        />
      ),
    },
    {
      label: 'Tư thế',
      element: (
        <Box display="flex">
          <Form.MuiTextField
            select
            required={false}
            disabled={disabled}
            sx={{ width: `${postureEtc ? '50%' : '100%'}` }}
            defaultValue={
              [...position_labels, undefined].includes(
                getValues('operation_information.position')
              )
                ? getValues('operation_information.position')
                : 'etc'
            }
            {...register('operation_information.position')}
            onChange={e => {
              if (e.target.value === 'etc') setPostureEtc(1);
              else setPostureEtc(0);
            }}
          >
            {position_labels.map(v => (
              <MenuItem value={v}>{v} position</MenuItem>
            ))}
            <MenuItem value="etc">Nhập trực tiếp</MenuItem>
          </Form.MuiTextField>
          {postureEtc ? (
            <Form.MuiTextField
              {...register('operation_information.position_etc')}
              placeholder="Nhập trực tiếp"
              sx={{ marginLeft: '5px', width: '50%' }}
            />
          ) : null}
        </Box>
      ),
    },
    {
      label: 'NPO',
      element: (
        <Form.MuiTextField
          select
          required={false}
          disabled={disabled}
          defaultValue={getValues('operation_information.npo_status')}
          {...register('operation_information.npo_status')}
        >
          <MenuItem value="금식">NPO</MenuItem>
          <MenuItem value="금식안함">Không phải NPO</MenuItem>
        </Form.MuiTextField>
      ),
    },
    {
      label: 'Kháng sinh\ndự phòng',
      element: (
        <Form.MuiTextField
          select
          required={false}
          disabled={disabled}
          defaultValue={getValues(
            'operation_information.prophylactic_antibiotics'
          )}
          {...register('operation_information.prophylactic_antibiotics')}
        >
          <MenuItem value="Injection">Tiêm</MenuItem>
          <MenuItem value="None">Không</MenuItem>
        </Form.MuiTextField>
      ),
    },

    {
      label: 'Dị ứng',
      element: (
        <Box display="flex">
          <Form.MuiTextField
            {...register('operation_information.allergy')}
            required={false}
            sx={{ width: '37%' }}
            disabled={disabled}
          />
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                defaultChecked={getValues(
                  'operation_information.preoperative_xray'
                )}
                {...register('operation_information.preoperative_xray')}
                sx={{
                  '&.Mui-checked': {
                    color: '#2D73DC',
                  },
                }}
              />
            }
            label="Pre X-ray"
            sx={{ marginLeft: '20px' }}
          />
          <FormControlLabel
            disabled={disabled}
            control={
              <Checkbox
                defaultChecked={getValues(
                  'operation_information.preoperative_ekg'
                )}
                {...register('operation_information.preoperative_ekg')}
                sx={{
                  '&.Mui-checked': {
                    color: '#2D73DC',
                  },
                }}
              />
            }
            defaultValue={getValues('operation_information.preoperative_ekg')}
            label="Post EKG"
            sx={{ marginLeft: '20px' }}
          />
        </Box>
      ),
    },
  ];

  useEffect(() => {
    if (
      ![...position_labels, undefined].includes(
        getValues('operation_information.position')
      )
    ) {
      setValue(
        'operation_information.position_etc',
        getValues('operation_information.position')
      );
      setPostureEtc(1);
    }
  }, []);

  return (
    <>
      <SectionTitle title="Thông tin phẫu thuật" />
      <RowContainer xs={12}>
        {contents.map(({ label, element }) => {
          if (
            label === 'Chẩn đoán sau\nphẫu thuật' ||
            label === 'Kháng sinh\ndự phòng'
          ) {
            return (
              <>
                <RowContentPrewrap
                  title={label}
                  titleRatio={1}
                  childrenRatio={2}
                >
                  {element}
                </RowContentPrewrap>
              </>
            );
          }

          return (
            <>
              <RowContent
                title={label}
                titleRatio={1}
                childrenRatio={label === 'Dị ứng' ? 5 : 2}
              >
                {element}
              </RowContent>
            </>
          );
        })}
      </RowContainer>
    </>
  );
};

export default NewOpInfo;
